import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { AgentVersion } from '../services/agent.service';
import { getAgent } from '../services/agent.service';
import { useAuth } from '../useAuth';

interface VersionContextType {
  version: AgentVersion | null;
  isLoading: boolean;
  refreshVersion: () => Promise<void>;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

/**
 * Provider for managing agent version
 * Automatically fetches version when user is authenticated
 * Version is not stored in localStorage, always fetched fresh from API
 */
export const VersionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { auth } = useAuth();
  const [version, setVersion] = useState<AgentVersion | null>(null);
  // Set initial loading state to true if auth exists (need to fetch version)
  const [isLoading, setIsLoading] = useState(() => !!auth?.agent_id);

  /**
   * Refresh version manually
   */
  const refreshVersion = async () => {
    const currentAgentId = auth?.agent_id;
    if (!currentAgentId) {
      setVersion(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const agentResponse = await getAgent();
      setVersion(agentResponse.version || null);
    } catch (error) {
      console.error('Failed to fetch agent version:', error);
      setVersion(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch version when auth changes (login/logout) or when component mounts
  useEffect(() => {
    const currentAgentId = auth?.agent_id;

    if (currentAgentId) {
      // Fetch version when agent_id is available
      const fetchVersion = async () => {
        try {
          setIsLoading(true);
          const agentResponse = await getAgent();
          setVersion(agentResponse.version || null);
        } catch (error) {
          console.error('Failed to fetch agent version:', error);
          setVersion(null);
        } finally {
          setIsLoading(false);
        }
      };
      fetchVersion();
    } else {
      // Clear version when user logs out
      setVersion(null);
      setIsLoading(false);
    }
  }, [auth?.agent_id]); // Re-fetch when auth object changes

  return (
    <VersionContext.Provider value={{ version, isLoading, refreshVersion }}>
      {children}
    </VersionContext.Provider>
  );
};

/**
 * Hook to access version context
 * @returns Version context with version, isLoading, and refreshVersion function
 */
export const useVersion = (): VersionContextType => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('アプリの情報を取得できませんでした。もう一度お試しください。');
  }
  return context;
};
